import { useLayoutEffect, useSyncExternalStore } from "react";
import {
  getShapeSettings,
  SHAPE_DASH_PRESETS,
  SHAPE_FILL_PRESETS,
  SHAPE_STROKE_PRESETS,
  SHAPE_WIDTH_PRESETS,
  setShapeSettings,
  shapeSettingsEventName,
} from "../../editor/shapeSession";

function subscribeShapeSettings(onStoreChange: () => void) {
  const handler = () => onStoreChange();
  window.addEventListener(shapeSettingsEventName(), handler);
  return () => window.removeEventListener(shapeSettingsEventName(), handler);
}

function notifyShapeSettings() {
  window.dispatchEvent(new Event(shapeSettingsEventName()));
}

export function BoardShapeOptions() {
  const shape = useSyncExternalStore(
    subscribeShapeSettings,
    getShapeSettings,
    getShapeSettings
  );

  function patch(partial: Parameters<typeof setShapeSettings>[0]) {
    setShapeSettings(partial);
    notifyShapeSettings();
  }

  useLayoutEffect(() => {
    window.dispatchEvent(new Event("wb:tool-options-layout"));
  }, []);

  return (
    <>
      <div className="wb-pen-options" role="group" aria-label="Параметры фигуры" onPointerDown={(ev) => ev.stopPropagation()}>
      <div className="wb-pen-options__swatches" role="group" aria-label="Заливка">
        {SHAPE_FILL_PRESETS.map((c) => (
          <button
            key={c}
            type="button"
            className={`wb-pen-options__swatch${shape.fill === c ? " is-active" : ""}`}
            style={{
              background: c === "none" ? "#fff" : c,
              boxShadow:
                c === "none" || c === "#ffffff"
                  ? "inset 0 0 0 1px #cbd5e1"
                  : undefined,
            }}
            title={c === "none" ? "Без заливки" : c}
            aria-pressed={shape.fill === c}
            onClick={() => patch({ fill: c })}
          />
        ))}
      </div>
      <div className="wb-pen-options__swatches" role="group" aria-label="Контур">
        {SHAPE_STROKE_PRESETS.map((c) => (
          <button
            key={c}
            type="button"
            className={`wb-pen-options__swatch${shape.stroke === c ? " is-active" : ""}`}
            style={{
              background: c,
              boxShadow: c === "#ffffff" ? "inset 0 0 0 1px #cbd5e1" : undefined,
            }}
            title={c}
            aria-pressed={shape.stroke === c}
            onClick={() => patch({ stroke: c })}
          />
        ))}
      </div>
      <div className="wb-pen-options__dashes" role="group" aria-label="Стиль контура">
        {SHAPE_DASH_PRESETS.map((d) => (
          <button
            key={d.id}
            type="button"
            className={`wb-pen-options__dash${(shape.strokeDash ?? "solid") === d.id ? " is-active" : ""}`}
            title={d.title}
            aria-pressed={(shape.strokeDash ?? "solid") === d.id}
            onClick={() => patch({ strokeDash: d.id })}
          >
            {d.label}
          </button>
        ))}
      </div>
      <div className="wb-pen-options__dashes" role="group" aria-label="Центр фигуры">
        <button
          type="button"
          className={`wb-pen-options__dash${shape.showCenter ? " is-active" : ""}`}
          title="Точка в центре плоской фигуры (у объёмных не рисуется)"
          aria-pressed={Boolean(shape.showCenter)}
          onClick={() => patch({ showCenter: !shape.showCenter })}
        >
          ⊙
        </button>
      </div>
      <div className="wb-pen-options__widths" role="group" aria-label="Толщина контура">
        {SHAPE_WIDTH_PRESETS.map((w) => (
          <button
            key={w}
            type="button"
            className={`wb-pen-options__width${shape.strokeWidth === w ? " is-active" : ""}`}
            title={`${w}px`}
            aria-pressed={shape.strokeWidth === w}
            onClick={() => patch({ strokeWidth: w })}
          >
            <span
              className="wb-pen-options__width-dot"
              style={{ width: w + 4, height: w + 4, background: shape.stroke }}
            />
          </button>
        ))}
      </div>
      </div>
    </>
  );
}
